import React from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '@/components/layout'
import { useAuth } from '@/hooks/useAuth'
import { useMyCommissions, useMyBalance } from '@/hooks/useCommission'
import { useAffiliateByUserID, useAffiliateStats } from '@/hooks/useAffiliate'

interface StatCardProps {
    icon: string
    label: string
    value: string | number
    trend?: string
    trendUp?: boolean
}

const StatCard: React.FC<StatCardProps> = ({ icon, label, value, trend, trendUp }) => (
    <div className="flex flex-col gap-1 rounded-lg p-5 bg-white dark:bg-surface-card shadow-sm border border-gray-200 dark:border-white/5">
        <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
                <span className="material-symbols-outlined text-primary text-[20px]">{icon}</span>
                <p className="text-gray-500 dark:text-text-muted text-xs font-medium uppercase tracking-wider">{label}</p>
            </div>
            {trend && (
                <span className={`text-xs font-bold ${trendUp ? 'text-green-400' : 'text-red-400'}`}>
                    {trendUp ? '↑' : '↓'} {trend}
                </span>
            )}
        </div>
        <p className="text-gray-900 dark:text-white text-3xl font-bold leading-tight">{value}</p>
    </div>
)

export const AffiliateDashboardPage: React.FC = () => {
    const { user } = useAuth()

    // Get affiliate info
    const { data: affiliate, isLoading: affiliateLoading } = useAffiliateByUserID(user?.id || '')

    // Get commissions and balance
    const { data: commissions, isLoading: commissionsLoading } = useMyCommissions(user?.id || '')
    const { data: balance, isLoading: balanceLoading } = useMyBalance(user?.id || '')

    // Get click stats
    const { data: clickStats, isLoading: statsLoading } = useAffiliateStats(affiliate?.id || '')

    // Calculate metrics
    const totalEarnings = balance?.earnedBalance || 0
    const earnedBalance = balance?.pendingBalance || 0
    const totalClicks = clickStats?.totalClicks || 0

    // Calculate conversions (commissions that are not cancelled)
    const conversions = commissions?.filter((c: any) => c.status !== 'cancelled')?.length || 0
    const conversionRate = totalClicks > 0 ? ((conversions / totalClicks) * 100).toFixed(1) : '0.0'

    const isLoading = affiliateLoading || commissionsLoading || balanceLoading || statsLoading

    return (
        <div>
            <PageHeader
                title={`Welcome, ${user?.name || 'Affiliate'}!`}
                subtitle="Track your referrals and earnings"
                badge="Affiliate"
                badgeVariant="green"
            />

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {isLoading ? (
                    <>
                        {[1, 2, 3, 4].map((i) => (
                            <div key={i} className="h-28 rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 animate-pulse" />
                        ))}
                    </>
                ) : (
                    <>
                        <StatCard icon="ads_click" label="Total Clicks" value={totalClicks.toLocaleString('id-ID')} />
                        <StatCard icon="person_add" label="Conversions" value={conversions} />
                        <StatCard icon="percent" label="Conversion Rate" value={`${conversionRate}%`} />
                        <StatCard icon="account_balance_wallet" label="Available Balance" value={`Rp ${earnedBalance.toLocaleString('id-ID')}`} />
                    </>
                )}
            </div>

            {/* Quick Actions */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                {/* Recent Referrals */}
                <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 overflow-hidden">
                    <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-white/5">
                        <h3 className="font-bold text-gray-900 dark:text-white">Recent Referrals</h3>
                        <Link to="/affiliate/tracking" className="text-xs font-medium text-primary hover:underline">View All</Link>
                    </div>
                    <div className="divide-y divide-gray-200 dark:divide-white/5">
                        {isLoading ? (
                            <div className="px-5 py-8 text-center text-gray-500 dark:text-text-muted">
                                <div className="animate-pulse">Loading...</div>
                            </div>
                        ) : commissions && commissions.length > 0 ? (
                            commissions.slice(0, 3).map((commission: any) => (
                                <div key={commission.id} className="flex items-center gap-4 px-5 py-4 hover:bg-gray-100 dark:hover:bg-white/5 transition-colors">
                                    <div className="size-10 rounded-full bg-gradient-to-br from-green-500/30 to-green-500/10 flex items-center justify-center">
                                        <span className="material-symbols-outlined text-green-400 text-[18px]">check</span>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-bold text-gray-900 dark:text-white truncate">Commission earned</p>
                                        <p className="text-xs text-gray-500 dark:text-text-muted">Amount: Rp {commission.amount?.toLocaleString('id-ID') || 0}</p>
                                    </div>
                                    <span className="text-xs text-gray-500 dark:text-text-muted">{commission.status}</span>
                                </div>
                            ))
                        ) : (
                            <div className="px-5 py-8 text-center text-gray-500 dark:text-text-muted">
                                <p className="text-sm">No referrals yet</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Affiliate Info */}
                <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 overflow-hidden">
                    <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-white/5">
                        <h3 className="font-bold text-gray-900 dark:text-white">Affiliate Info</h3>
                        <Link to="/affiliate/links" className="text-xs font-medium text-primary hover:underline">Manage Links</Link>
                    </div>
                    <div className="p-5">
                        {isLoading ? (
                            <div className="animate-pulse">
                                <div className="h-4 bg-gray-100 dark:bg-white/10 rounded mb-2" />
                                <div className="h-6 bg-gray-100 dark:bg-white/10 rounded w-3/4" />
                            </div>
                        ) : affiliate ? (
                            <div className="space-y-3">
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-text-muted mb-1">Affiliate Code</p>
                                    <p className="text-lg font-bold text-red-400">{affiliate.code}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-text-muted mb-1">Commission Type</p>
                                    <p className="text-sm font-medium text-gray-900 dark:text-white">
                                        {affiliate.commissionType === 'percentage'
                                            ? `${affiliate.commissionValue}% per sale`
                                            : `Rp ${affiliate.commissionValue?.toLocaleString('id-ID')} per sale`}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-text-muted mb-1">Status</p>
                                    <span className={`text-xs font-bold px-2 py-1 rounded ${affiliate.isActive ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                                        {affiliate.isActive ? 'Active' : 'Inactive'}
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-4 text-gray-500 dark:text-text-muted">
                                <p className="text-sm">No affiliate account found</p>
                                <p className="text-xs mt-1">Contact admin to create one</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Earnings Summary */}
            <div className="rounded-lg bg-white dark:bg-surface-card border border-gray-200 dark:border-white/5 p-6">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="font-bold text-gray-900 dark:text-white">Earnings Summary</h3>
                    <Link to="/affiliate/earnings" className="text-xs font-medium text-primary hover:underline">View Details</Link>
                </div>
                <div className="flex flex-col md:flex-row items-center gap-6">
                    <div className="flex-1 text-center md:text-left">
                        <p className="text-gray-500 dark:text-text-muted text-sm mb-1">Total Earned (All Time)</p>
                        <p className="text-3xl font-bold text-red-400">Rp {totalEarnings.toLocaleString('id-ID')}</p>
                    </div>
                    <Link
                        to="/affiliate/withdraw"
                        className="px-6 py-3 bg-primary text-white rounded-lg font-bold text-sm hover:scale-105 transition-transform"
                    >
                        Request Withdrawal
                    </Link>
                </div>
            </div>
        </div>
    )
}

export default AffiliateDashboardPage
